export function modalQueryId(value) {
  if (typeof value !== 'string' || !/^[0-9]+$/.test(value)) return null
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

function modalFlow(query) {
  if (query.flow === undefined) return 'v2'
  return ['v2', 'legacy'].includes(query.flow) ? query.flow : null
}

function normalizeModalQuery(source, queryKey, withFlow) {
  const query = { ...source }
  const flow = modalFlow(query)
  const hasId = Object.prototype.hasOwnProperty.call(query, queryKey)
  const id = modalQueryId(query[queryKey])
  let error = ''
  let replace = false

  if (withFlow && !flow) {
    query.flow = 'v2'
    delete query[queryKey]
    error = 'Invalid conversation link: flow must be v2 or legacy. Select the conversation again.'
    return { query, flow, id: null, error, replace: true }
  }

  if (hasId && !id) {
    delete query[queryKey]
    error = `Invalid ${queryKey} link: use one positive integer ID.`
    replace = true
  }
  if (withFlow && hasId && query.flow === undefined) {
    query.flow = 'v2'
    replace = true
  }
  return { query, flow, id, error, replace }
}

function historyKey(create = false) {
  const state = window.history.state || {}
  if (!state.crmModalEntry && create) {
    // Vue Router preserves custom state on replace, but starts fresh on push.
    const key = `${Date.now()}-${Math.random()}`
    window.history.replaceState({ ...state, crmModalEntry: key }, '')
    return key
  }
  return state.crmModalEntry
}

// Shared by the two CRM pages whose selected record is owned by the URL.
export function modalRoute(queryKey, withFlow = false) {
  return {
    data() {
      return { modalNavigationPending: false }
    },
    beforeCreate() {
      this.modalEntries = new Map()
      this.modalTrigger = null
      this.removeModalReturnHook = null
    },
    computed: {
      modalRouteState() {
        return normalizeModalQuery(this.$route.query, queryKey, withFlow)
      },
      modalFlow() {
        return this.modalRouteState.flow
      },
      selectedModalId() {
        return this.modalRouteState.id
      }
    },
    watch: {
      '$route': {
        immediate: true,
        handler(route) {
          const { query, error, replace } = this.modalRouteState
          if (error) this.$notify({ type: 'error', title: error })
          if (replace) this.$router.replace({ path: route.path, query, hash: route.hash }).catch(() => {})
        }
      },
      selectedModalId(id, previousId) {
        if (!id && previousId) this.restoreModalFocus()
      }
    },
    beforeDestroy() {
      if (this.removeModalReturnHook) this.removeModalReturnHook()
      this.modalEntries.clear()
    },
    methods: {
      async openRouteModal(id, trigger) {
        const validId = modalQueryId(String(id))
        if (!validId || this.modalNavigationPending || validId === this.selectedModalId) return
        const source = this.$route
        const sourceKey = historyKey(true)
        const query = { ...source.query, [queryKey]: String(validId) }
        if (withFlow) query.flow = this.modalFlow || 'v2'
        this.modalTrigger = trigger || document.activeElement
        this.modalNavigationPending = true
        try {
          await this.$router.push({ path: source.path, query, hash: source.hash })
          const key = historyKey(true)
          if (key && sourceKey && !Object.prototype.hasOwnProperty.call(source.query, queryKey)) {
            this.modalEntries.set(key, { sourceKey, path: source.path })
          }
        } catch (error) {
          if (!error || error.name !== 'NavigationDuplicated') {
            this.$notify({ type: 'error', title: 'Unable to open this link. Please try again.' })
          }
        } finally {
          this.modalNavigationPending = false
        }
      },
      closeRouteModal() {
        if (!this.selectedModalId || this.modalNavigationPending) return
        const { path, hash } = this.$route
        const query = { ...this.$route.query }
        delete query[queryKey]
        const entry = this.modalEntries.get(historyKey())
        if (entry && entry.path === path) {
          this.modalNavigationPending = true
          this.removeModalReturnHook = this.$router.afterEach(to => {
            this.removeModalReturnHook()
            this.removeModalReturnHook = null
            this.modalNavigationPending = false
            // A manager/filter may have been replaced while the modal was open.
            // Keep it when returning to the original history entry.
            if (to.path === path && historyKey() === entry.sourceKey) {
              const target = { path, query, hash }
              if (this.$router.resolve(target).route.fullPath !== to.fullPath) {
                this.$router.replace(target).catch(() => {})
              }
            }
          })
          this.$router.back()
        } else {
          this.$router.replace({ path, query, hash }).catch(() => {})
        }
      },
      restoreModalFocus() {
        this.$nextTick(() => {
          if (this._isDestroyed || this.selectedModalId) return
          const trigger = this.modalTrigger
          const target = trigger && trigger !== document.body && document.documentElement.contains(trigger) ? trigger : this.$el
          if (target && target.focus) {
            if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
            target.focus()
          }
        })
      }
    }
  }
}
